package com.hyperether.goodjob

import androidx.compose.runtime.MutableState
import androidx.compose.runtime.mutableStateOf
import androidx.navigation.NavHostController
import com.hyperether.goodjob.database.ContactDaoImpl
import com.hyperether.goodjob.database.JobDaoImpl
import com.hyperether.goodjob.database.PlanDaoImpl
import com.hyperether.goodjob.database.SharedDatabase
import com.hyperether.goodjob.database.SiteDaoImpl
import com.hyperether.goodjob.database.SkillsDaoImpl
import com.hyperether.goodjob.database.TeamDaoImpl
import com.hyperether.goodjob.database.UserDaoImpl
import com.hyperether.goodjob.database.WorkspaceDaoImpl
import com.hyperether.goodjob.managers.FileManager
import com.hyperether.goodjob.managers.NetworkManager
import com.hyperether.goodjob.navigation.Pricing
import com.hyperether.goodjob.navigation.Register
import com.hyperether.goodjob.navigation.RouteScreen
import com.hyperether.goodjob.navigation.Splash
import com.hyperether.goodjob.navigation.Workspace
import com.hyperether.goodjob.repository.Repository
import com.hyperether.goodjob.repository.RepositoryImpl
import com.hyperether.goodjob.repository.WebRepositoryImpl
import com.hyperether.goodjob.repository.prefs.PrefsManager
import com.hyperether.goodjob.repository.remote.GoodJobApiImpl
import com.hyperether.goodjob.repository.remote.model.EphemeralKeyRequest
import com.hyperether.goodjob.repository.remote.model.ResetPasswordRequest
import com.hyperether.goodjob.repository.remote.model.Resource
import com.hyperether.goodjob.repository.remote.model.SetPasswordRequest
import com.hyperether.goodjob.scenes.addNew.AddContactViewModel
import com.hyperether.goodjob.scenes.addNew.AddJobViewModel
import com.hyperether.goodjob.scenes.addNew.AddNewViewModel
import com.hyperether.goodjob.scenes.addNew.AddUserViewModel
import com.hyperether.goodjob.scenes.contacts.ContactsViewModel
import com.hyperether.goodjob.scenes.dashboard.DashboardViewModel
import com.hyperether.goodjob.scenes.jobs.JobListViewModel
import com.hyperether.goodjob.scenes.map.MapViewModel
import com.hyperether.goodjob.scenes.pricing.PricingViewModel
import com.hyperether.goodjob.scenes.register.RegisterViewModel
import com.hyperether.goodjob.scenes.scheduler.SchedulerViewModel
import com.hyperether.goodjob.scenes.user.UserViewModel
import com.hyperether.goodjob.scenes.workspace.WorkspaceViewModel
import com.hyperether.goodjob.util.Logger
import com.hyperether.goodjob.util.UploadManager
import io.ktor.util.PlatformUtils.IS_BROWSER
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.launch


class CommonModule(
    val prefsManager: PrefsManager,
    val networkManager: NetworkManager,
    val fileManager: FileManager,
    val sharedDatabase: SharedDatabase,
    val platform: Platform,
    val currentRoute: MutableState<RouteScreen> = mutableStateOf(Splash),
) {

    lateinit var repository: Repository
    lateinit var navHostController: NavHostController
    lateinit var pricingViewModel: PricingViewModel
    lateinit var workspaceViewModel: WorkspaceViewModel
    lateinit var registerViewModel: RegisterViewModel
    lateinit var userViewModel: UserViewModel
    lateinit var addContactViewModel: AddContactViewModel
    lateinit var addJobViewModel: AddJobViewModel
    lateinit var addUserViewModel: AddUserViewModel
    lateinit var addNewViewModel: AddNewViewModel
    lateinit var jobListViewModel: JobListViewModel
    lateinit var dashboardViewModel: DashboardViewModel
    lateinit var schedulerViewModel: SchedulerViewModel
    lateinit var dashboardMapViewModel: MapViewModel
    lateinit var contactsViewModel: ContactsViewModel
    lateinit var mapViewModel: MapViewModel

    var passwordInput = mutableStateOf("")
    var confirmPasswordInput = mutableStateOf("")
    var showSetNewPasswordView = mutableStateOf(false)
    var showSetPasswordView = mutableStateOf(false)
    var showPasswordSetSuccessDialog = mutableStateOf(false)
    var setPasswordHash: MutableState<String?> = mutableStateOf(null)

    var errorText = mutableStateOf("")
    var showErrorDialog = mutableStateOf(false)
    var showPasswordErrorDialog = mutableStateOf(false)
    var showPasswordUpdatedSuccessDialog = mutableStateOf(false)

    var showPlanChangedDialog = mutableStateOf(false)
    var showMaxSizeDialog = mutableStateOf(false)
    var showFileAddedSuccess = mutableStateOf(false)
    var showLoader = mutableStateOf(false)

    var token: MutableState<String?> = mutableStateOf(null)

    // Flag to trigger drawer close from UI layer
    var shouldCloseDrawer = mutableStateOf(false)

    enum class Platform {
        ANDROID, IOS, WEB
    }

    init {
        log.d { "CommonModule instance" }
        init()
    }

    fun init() {
        val workspaceDao = WorkspaceDaoImpl(sharedDatabase)
        val userDao = UserDaoImpl(sharedDatabase)
        val jobDao = JobDaoImpl(sharedDatabase)
        val planDao = PlanDaoImpl(sharedDatabase)
        val siteDao = SiteDaoImpl(sharedDatabase)
        val contactDao = ContactDaoImpl(sharedDatabase)
        val skillsDao = SkillsDaoImpl(sharedDatabase)
        val teamDao = TeamDaoImpl(sharedDatabase)
        val goodJobApiImpl = GoodJobApiImpl()

        if (platform == Platform.WEB) {
            repository = WebRepositoryImpl(prefsManager, goodJobApiImpl, fileManager)
        } else {
            repository = RepositoryImpl(
                workspaceDao,
                userDao,
                planDao,
                jobDao,
                contactDao,
                siteDao,
                skillsDao,
                prefsManager,
                goodJobApiImpl,
                teamDao,
                fileManager
            )
        }
    }

    fun clear() {
        userViewModel.clearFilterValues()
        contactsViewModel.clearFilterValues()
        mapViewModel.resetFilter()

        jobListViewModel.resetFilter()
        jobListViewModel.filteredJobs.clear()

        schedulerViewModel.clear()
        schedulerViewModel.resetFilter()
        schedulerViewModel.filteredJobs.clear()

        dashboardViewModel.resetFilter()
        dashboardViewModel.filteredJobs.clear()
        dashboardViewModel.listOfPlans.clear()
    }

    fun getStartRoute(): RouteScreen {
        val startRoute: RouteScreen =
            if (IS_BROWSER) {
                if (repository.getUser() != null && repository.getUser()?.planId?.isNotEmpty() == true && prefsManager.getWorkspaceId() == "") {
                    Workspace
                } else if (repository.getUser() != null && repository.getUser()?.planId?.isEmpty() == true) {
                    Pricing
                } else {
                    currentRoute.value
                }
            } else {
                currentRoute.value
            }
        return startRoute
    }

    fun setPasswordCoroutine() {
        CoroutineScope(Dispatchers.Main).launch {
            setPassword()
        }
    }

    fun resetPasswordCoroutine() {
        CoroutineScope(Dispatchers.Main).launch {
            resetPassword()
        }
    }

    suspend fun setPassword() {
        val result = repository.setPassword(
            SetPasswordRequest(
                password = passwordInput.value,
                confirm_password = confirmPasswordInput.value,
                hash = setPasswordHash.value
            )
        )
        when (result) {
            is Resource.Success -> {
                showSetPasswordView.value = false
                showPasswordSetSuccessDialog.value = true
            }

            is Resource.Error -> {
                showSetPasswordView.value = false
                showPasswordErrorDialog.value = true
                errorText.value = result.text.toString()
            }
        }
    }

    suspend fun resetPassword() {
        val result = repository.resetPassword(
            ResetPasswordRequest(
                repository.getEmail(),
                token.value,
                confirmPasswordInput.value
            )
        )
        when (result) {
            is Resource.Success -> {
                showSetNewPasswordView.value = false
                showPasswordUpdatedSuccessDialog.value = true
            }

            is Resource.Error -> {
                showSetNewPasswordView.value = false
                showPasswordErrorDialog.value = true
                errorText.value = result.text.toString()
            }
        }
    }

    fun onChoosePlan(
        amount: Double,
        onSuccess: (PaymentInfo) -> Unit,
        onUserLogin: () -> Unit
    ) {
        if (amount == 0.0) {
            pricingViewModel.subscribeToPlan(
                onSuccess = { response ->
                    log.d("onChoosePlan") { "planId: ${response.message} isNavigationSourceDrawer: ${pricingViewModel.isNavigationSourceDrawer.value} pricingViewModel: ${pricingViewModel}" }
                    if (pricingViewModel.isNavigationSourceDrawer.value) {
                        response.message?.let { successMessage ->
                            pricingViewModel.messageTextChangePlan.value = successMessage
                            showPlanChangedDialog.value = true
                        }
                    } else {
                        navHostController.navigate(Workspace)
                    }
                },
                onError = { error ->
                    showErrorDialog.value = true
                    errorText.value = error
                },
                onUserLogin = {
                    onUserLogin()
                })
        } else {
            val customerId = repository.getUser()?.customerId ?: return
            pricingViewModel.getEphemeralKey(
                EphemeralKeyRequest(customerId = customerId),
                onSuccess = { key ->
                    pricingViewModel.setupPaymentIntent(
                        onSuccess = { clientSecret ->
                            key.ephemeralKey?.secret?.let { ephemeralKey ->
                                onSuccess(PaymentInfo(customerId, clientSecret, ephemeralKey))
                            }
                        },
                        onError = { errorMessage ->
                            Logger.e("Payment", errorMessage)
                            showErrorDialog.value = true
                            errorText.value = errorMessage
                        },
                        onUserLogin = {
                            onUserLogin()
                        }
                    )
                },
                onError = { errorMessage ->
                    Logger.e("Payment", errorMessage)
                    showErrorDialog.value = true
                    errorText.value = errorMessage
                },
                onUserLogin = {
                    onUserLogin()
                }
            )
        }
    }

    fun onPayment() {
        log.d { "onPayment" }
        pricingViewModel.showLoader.value = true
        pricingViewModel.subscribeToPlan(onSuccess = { response ->
            if (IS_BROWSER || pricingViewModel.isNavigationSourceDrawer.value) {
                log.d { "onPayment: present message" }
                response.message?.let {
                    pricingViewModel.messageTextChangePlan.value = it
                    showPlanChangedDialog.value = true
                    log.d { "onPayment: showPlanChangedDialog: ${showPlanChangedDialog.value}" }
                }
            } else {
                navHostController.navigate(Workspace)
            }
            pricingViewModel.showLoader.value = false
        }, onError = { errText ->
            log.d { "onPayment: present error" }
            pricingViewModel.error.value = Resource.Error(errText)
            pricingViewModel.showLoader.value = false
            errorText.value = errText
            showErrorDialog.value = true
        }, onUserLogin = {})
    }

    fun logout(onSuccess: () -> Unit) {
        CoroutineScope(Dispatchers.Main).launch {
            if (repository.getUser() != null) {
                log.d { "User exist, logout" }
                UploadManager.removeAllFiles()
                repository.logoutUser()
                currentRoute.value = Register
                clear()
                // TODO: check if this is needed
                //init()
                onSuccess()
            }
        }
    }
}

data class PaymentInfo(
    val customerId: String,
    val clientSecret: String,
    val ephemeralKey: String
)
